/**
 * AgentClient SDK — pure reducer + derivations (adr-039).
 *
 * `reduce(state, event)` is total and side-effect-free: every event either
 * produces a valid next state or is ignored (invalid transitions are rejected,
 * never guessed). This is the whole race-immunity story — a stale/duplicate/
 * out-of-order event that doesn't fit the current phase is simply a no-op.
 */
import type {
  AgentEvent, Availability, CallPhase, CallView, ChosenState, InteractionRef, LinkState, WrapReason,
} from "./types";

interface Core {
  chosen: ChosenState;
  phase: CallPhase;
  interaction: InteractionRef | null;
  muted: boolean;
  recordingPaused: boolean;
  recordingError: string | null;
  transferInFlight: boolean;
  wrapReason: WrapReason | null;
  link: LinkState;
  dialError: string | null;
}

export const initialCore: Core = {
  chosen: "OFFLINE",
  phase: "idle",
  interaction: null,
  muted: false,
  recordingPaused: false,
  recordingError: null,
  transferInFlight: false,
  wrapReason: null,
  dialError: null,
  // Assumed reachable until a probe says otherwise, so a desktop that has not
  // yet completed its first probe does not flash "offline" on load.
  link: "up",
};

// Phases where a live/originating call exists → hangup/caller-drop moves to wrap.
// All three pre-answer outbound phases count: the leg is out, the agent is on
// it, and Cancel has to reach it from any of them.
const OUTBOUND_PRE_ANSWER: CallPhase[] = ["dialing", "connecting", "ringback"];
const IN_CALL: CallPhase[] = [...OUTBOUND_PRE_ANSWER, "connected", "hold"];

/** Derived presence: BUSY on an active/originating call, WRAP_UP during
 *  after-call work, else the agent's chosen base state. Keeps availability and
 *  phase in lockstep (this is exactly what the presence heartbeat reports). */
export function availabilityOf(c: Core): Availability {
  if (IN_CALL.includes(c.phase)) return "BUSY";
  if (c.phase === "wrap") return "WRAP_UP";
  // A desktop that cannot reach the backend must not claim to be taking calls.
  // Reported as OFFLINE rather than the chosen state: the ACD will go on
  // offering work to an AVAILABLE agent, and every one of those offers rings
  // nowhere while the caller waits on hold.
  //
  // Only the idle presentation is suppressed. An in-progress call is NOT
  // downgraded above — media runs over its own path and survives a control-plane
  // blip, so telling the agent they are BUSY stays true.
  //
  // `chosen` is deliberately left alone (see the reducer below), so recovery
  // restores availability without the agent re-clicking anything.
  if (c.link === "down") return "OFFLINE";
  return c.chosen;
}

export function snapshot(c: Core): CallView {
  return { ...c, availability: availabilityOf(c) };
}

/** Clean idle core, preserving the agent's chosen base state. */
function toIdle(c: Core): Core {
  return {
    ...c, phase: "idle", interaction: null, muted: false,
    recordingPaused: false, recordingError: null, transferInFlight: false, wrapReason: null,
    dialError: null,
  };
}

function wrap(c: Core, reason: WrapReason): Core {
  // The call is over — in-call flags no longer apply; only after-call work remains.
  return {
    ...c, phase: "wrap", muted: false, recordingPaused: false, recordingError: null,
    transferInFlight: false, wrapReason: reason,
  };
}

export function reduce(c: Core, e: AgentEvent): Core {
  switch (e.type) {
    case "set_availability":
      // Base state only changes when not mid-call; a call drives BUSY/WRAP_UP.
      return c.phase === "idle" ? { ...c, chosen: e.value } : c;

    case "link_lost": {
      if (c.link === "down") return c;
      // A ringing offer does not survive the link. Accepting one needs the
      // backend, so a ring the agent cannot answer is worse than no ring: they
      // reach for it, it fails, and the caller has already been re-routed by
      // RONA. Drop back to idle and let the offer find someone reachable.
      const dropped = c.phase === "ringing" ? toIdle(c) : c;
      return { ...dropped, link: "down" };
    }

    case "link_restored":
      // `chosen` was never touched, so an agent who was AVAILABLE before the
      // blip is available again here — without re-clicking, and without being
      // silently re-enlisted if they had gone NOT_READY in the meantime.
      return c.link === "up" ? c : { ...c, link: "up" };

    case "dial_started":
      // Outbound (adr-050): the agent originates from idle. Presence derives BUSY.
      // Clears any previous failure: the message belongs to the attempt that
      // produced it, and leaving it up next to a live call is a lie.
      return c.phase === "idle"
        ? { ...c, phase: "dialing", interaction: e.interaction, dialError: null } : c;

    case "call_progress": {
      // Carrier progress only means anything on a leg that is still ringing
      // out; anything else is a late or duplicate event and is ignored, which
      // is this reducer's whole race story.
      if (!OUTBOUND_PRE_ANSWER.includes(c.phase)) return c;
      if (e.status === "active") {
        return {
          ...c, phase: "connected", muted: false, recordingPaused: false,
          recordingError: null, transferInFlight: false, dialError: null,
        };
      }
      if (e.status === "hangup") {
        // Hung up BEFORE answer: nobody picked up, or the carrier refused it.
        // Back to idle carrying the reason — a desk that silently returns to
        // idle looks exactly like a button that did not register.
        return { ...toIdle(c), dialError: e.reason || "the call ended before it was answered" };
      }
      const next: CallPhase = e.status === "ringing" ? "ringback" : "connecting";
      // Never walk backwards: a duplicate `dialing` after `ringing` must not
      // un-ring the card.
      const order: CallPhase[] = OUTBOUND_PRE_ANSWER;
      return order.indexOf(next) > order.indexOf(c.phase) ? { ...c, phase: next } : c;
    }

    case "dial_failed":
      // From any pre-answer phase — the origination call itself failed, or the
      // media room could not be joined. Idle again, and able to say why.
      return OUTBOUND_PRE_ANSWER.includes(c.phase)
        ? { ...toIdle(c), dialError: e.reason }
        : c;

    case "offered":
      // Only an idle, available agent can be offered a call.
      return c.phase === "idle" && c.chosen === "AVAILABLE"
        ? { ...c, phase: "ringing", interaction: e.interaction }
        : c;

    case "offer_abandoned":
      // Caller hung up while ringing → after-call work (disposition), keep the
      // interaction so the agent can wrap it.
      return c.phase === "ringing" ? wrap(c, "caller_abandoned_ringing") : c;

    case "offer_rerouted":
      return c.phase === "ringing" ? toIdle(c) : c;

    case "answered":
      // Inbound accept (ringing) or outbound callee pickup (any pre-answer
      // phase, since a fast answer can arrive before we ever saw a ring).
      return c.phase === "ringing" || OUTBOUND_PRE_ANSWER.includes(c.phase)
        ? {
            ...c, phase: "connected", muted: false, recordingPaused: false,
            recordingError: null, transferInFlight: false,
            interaction: c.interaction ? { ...c.interaction, room_name: e.room ?? c.interaction.room_name } : c.interaction,
          }
        : c;

    case "agent_hangup":
      return IN_CALL.includes(c.phase) ? wrap(c, "agent_hangup") : c;

    case "caller_disconnected":
      return IN_CALL.includes(c.phase) ? wrap(c, "caller_disconnected") : c;

    case "held":
      return c.phase === "connected" ? { ...c, phase: "hold" } : c;

    case "resumed":
      return c.phase === "hold" ? { ...c, phase: "connected" } : c;

    case "muted":
      return IN_CALL.includes(c.phase) ? { ...c, muted: e.value } : c;

    case "recording_paused":
      return IN_CALL.includes(c.phase)
        ? { ...c, recordingPaused: e.value, recordingError: null } : c;
    case "recording_toggle_failed":
      // The pause state is deliberately NOT changed — it still reflects what the
      // media path is actually doing. Only the explanation is added.
      return IN_CALL.includes(c.phase) ? { ...c, recordingError: e.message } : c;

    case "transfer_started":
      return IN_CALL.includes(c.phase) ? { ...c, transferInFlight: true } : c;

    case "transfer_failed":
      return { ...c, transferInFlight: false };

    case "conferenced":
      // Warm add stays on the call; just clear the in-flight guard.
      return { ...c, transferInFlight: false };

    case "blind_transferred":
      return IN_CALL.includes(c.phase) ? wrap(c, "blind_transfer") : c;

    case "wrap_completed":
      return c.phase === "wrap" ? toIdle(c) : c;

    case "reset":
      return toIdle(c);

    default: {
      // Exhaustiveness: a new event type must be handled above.
      const _never: never = e;
      return _never;
    }
  }
}
